import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './google-translate.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';

export default class GoogleTranslate extends OutlineElement {
  static styles = [super.styles, componentStyles];
  static get properties() {
    return {
      currentLanguage: { type: String },
      isAppendedOnMobile: { type: Boolean },
      isAppendedOnDesktop: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.currentLanguage = 'en';
    this.isAppendedOnMobile = false;
    this.isAppendedOnDesktop = false;

    this.updateGlobalScope();
    this.createTranslateElement();

    window.addEventListener('load', () => {
      this.checkMobile();
      window.addEventListener('resize', () => this.checkMobile());
      this.setupIframe(); // Setup iframe on load
      this.observeIframe(); // Start observing the iframe
      this.addScrollListener(); // Add scroll listener
    });
  }

  googleTranslateInit() {
    new google.translate.TranslateElement(
      {
        pageLanguage: window.currentLanguage,
        layout: google.translate.TranslateElement.InlineLayout.SIMPLE,
        includedLanguages: 'af,sq,am,ar,hy,az,eu,be,bn,bs,bg,ca,ceb,ny,zh-CN,zh-TW,co,hr,cs,da,nl,en,eo,et,tl,fi,fr,fy,gl,ka,de,el,gu,ht,ha,haw,iw,hi,hmn,hu,is,ig,id,ga,it,ja,jw,kn,kk,km,ko,ku,ky,lo,la,lv,lt,lb,mk,mg,ms,ml,mt,mi,mr,mn,my,ne,no,ps,fa,pl,pt,pa,ro,ru,sm,gd,sr,st,si,sk,sl,so,es,su,sw,sv,ta,te,th,tg,tr,uk,ur,uz,vi,cy,xh,yi,yo,zu'
      },
      'google_translate_element'
    );
  }

  updateGlobalScope() {
    window.currentLanguage = this.currentLanguage;
    window.googleTranslateInit = this.googleTranslateInit;
  }

  createTranslateElement() {
    const googleTranslateElement = document.createElement('div');
    googleTranslateElement.innerHTML = `<div id="google_translate_element"></div>`;
    document.body.insertAdjacentElement('afterbegin', googleTranslateElement);
  }

  attachWidget() {
    let googleTranslateScript = document.createElement('script');
    googleTranslateScript.type = 'text/javascript';
    googleTranslateScript.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateInit';
    (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(googleTranslateScript);
  }

  checkMobile() {
    this.update();
    this.isMobile = window.innerWidth < 1024;

    if (this.isMobile) {
      this.appendOnMobile();
    } else {
      this.appendOnDesktop();
    }
  }

  appendOnMobile() {
    if (!this.isAppendedOnMobile) {
      let googleTranslate = this.getGoogleTranslateElement();
      this.isAppendedOnMobile = true;
      this.isAppendedOnDesktop = false;

      document
        .querySelector('hf-main-nav')
        .shadowRoot.querySelector('.nav-flyout-mobile')
        .shadowRoot.querySelector('.nav-flyout-mobile__secondary-menu-wrapper')
        .appendChild(googleTranslate);
    }
  }

  appendOnDesktop() {
    if (!this.isAppendedOnDesktop) {
      let googleTranslate = this.getGoogleTranslateElement();
      this.isAppendedOnDesktop = true;
      this.isAppendedOnMobile = false;

      document
        .querySelector('hf-icon-nav[navClass="secondary"]')
        .shadowRoot
        .querySelector('ul .icon-navigation__language')
        .insertAdjacentElement('afterbegin', googleTranslate);
    }
  }

  getGoogleTranslateElement() {
    return document.querySelector("#google_translate_element");
  }

  setupIframe() {
    // Select the iframe and container
    let iframe = document.querySelector('.VIpgJd-ZVi9od-xl07Ob-OEVmcd');
    let container = document.querySelector('body .ultrawide-layout-container');

    // Ensure the iframe and container are still available
    if (container && iframe) {
      const containerRect = container.getBoundingClientRect();

      // Set the position of the iframe
      iframe.style.left = `${containerRect.left}px`; // Align to left of container
      iframe.style.maxWidth = "1440px";
      iframe.style.width = "100%";

      // Access the iframe's document
      let iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

      // Create a new <style> element
      let style = document.createElement('style');
      style.innerHTML = `
        body > div {
          width: 100% !important;
          max-width: 1440px !important;
        }

        body > div table {
          width: 100%;
        }
      `;

      // Append the style element to the iframe's head
      if (iframeDocument.head) {
        iframeDocument.head.appendChild(style);
      }
    }
  }

  observeIframe() {
    const iframe = document.querySelector('.VIpgJd-ZVi9od-xl07Ob-OEVmcd');

    // Use MutationObserver to keep an eye on changes
    const observer = new MutationObserver(() => {
      this.setupIframe(); // Reapply the position and styles whenever a mutation is observed
      this.addScrollListener(); // Add scroll listener
    });

    // Observe the iframe for changes in attributes and child nodes
    if (iframe) {
      observer.observe(iframe, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    // Optionally, also call this function on window resize
    window.addEventListener('resize', () => this.setupIframe());
  }

  addScrollListener() {
    const iframe = document.querySelector('.VIpgJd-ZVi9od-xl07Ob-OEVmcd');

    window.addEventListener('scroll', () => {
      if (iframe) {
        iframe.style.display = 'none'; // Hide iframe on scroll
      }
    });
  }

  firstUpdated() {
    this.updateGlobalScope();
    this.attachWidget();
    this.setupIframe(); // Initial setup of the iframe
  }
}

customElements.define('hf-google-translate', GoogleTranslate);